/**
 * 시스템 설정 — 「지갑거래서명약관」 탭(EA-65)·판 보기(EA-66)·새 판(EA-67).
 *
 * 요구: 거래 흐름 04 3-3(`~/dev1/devnet-docs/_data/2026-09-30/trade-flow/04_고칠목록.md`) — 사장님
 * 「지갑에서는 api호출로 받아가야되고」, 「지갑에서 서명약관은 …/settings 이페이지에 메뉴만들어서
 * 지갑거래서명약관으로 메뉴 만들어서 관리해야지」. 탭 이름 「지갑거래서명약관」은 사장님 말 그대로다.
 *
 * 계약: XM-100(목록)·XM-101(시행 중)·XM-102(한 판) — 키 `terms.view`, XM-103(새 판) — 키 `terms.edit`.
 * 파트너·철회 약관(EA-42 약관 탭·EA-52·EA-53)과 같은 방식이다 — 고치면 새 판, 옛 판은 지우지 않는다.
 * 다른 점은 판마다 한국어·영어 두 글(제목·본문)이 있다는 것.
 *
 * 지갑은 지갑 앱 → 지갑백엔드 → 거래소 XW-15 로 시행 중인 판(XM-101 과 같은 판)을 받아 간다 —
 * 이 웹은 그 길에 끼지 않는다.
 */
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createWalletTradeTerms, getEffectiveWalletTradeTerms, getWalletTradeTerms, listWalletTradeTerms,
} from '@/api/admin'
import { useSession } from '@/auth/session'
import { setFlash } from '@/auth/pendingWrite'
import { AdminRef, Badge, Button, Field, Notice, Row, TD, TextArea, TH, Time } from '@/components/ui'
import { Confirm, submitWrite, useWrite, ViewDialog } from '@/components/Confirm'
import { QueryState } from '@/components/QueryState'
import { effectiveMillis, LangText, orNullOn404, Plain } from '@/components/Versioned'
import { DateTimeField } from '@/components/DateFields'
import type { WalletTradeTerms } from '@/api/types'

/** 탭·창 이름 — 사장님 말 그대로. */
export const WALLET_TRADE_TERMS = 'Wallet trade signing terms'

/** 어디에 보이는지(탭 안내·새 판 창). */
const WHERE = 'From the effective time, the wallet app shows this version as its trade signing terms.'

/** 시행 중인 판 — 없으면(404) null(배지 없이 목록만, EA-42 약관 탭과 같게). */
const effectiveOrNull = () => orNullOn404(getEffectiveWalletTradeTerms)

/** EA-65 — 지갑거래서명약관 탭(XM-100·XM-101). */
export function WalletTradeTermsTab() {
  const { has } = useSession()
  const list = useQuery({ queryKey: ['wallet-trade-terms'], queryFn: listWalletTradeTerms })
  const effective = useQuery({ queryKey: ['wallet-trade-terms', 'effective'], queryFn: effectiveOrNull })
  const [viewing, setViewing] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  // 판 새것부터(서버도 새 판 먼저 준다).
  const rows = [...(list.data ?? [])].sort((a, b) => b.version - a.version)
  const current = effective.data?.version ?? null

  return (
    <>
      <p className="mb-3 text-xs text-slate-500">
        The terms the wallet app shows before a trade is signed. Each version has a Korean and an English text. {WHERE}
        {' '}The consent check line and the button text are not part of the terms (they are wallet app text).
      </p>
      {has('terms.edit') && (
        <div className="mb-4"><Button onClick={() => setCreating(true)}>New version</Button></div>
      )}
      <QueryState isPending={list.isPending} error={list.error} empty={rows.length === 0}
        emptyText="There are no wallet trade signing terms.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>Version</th><th className={TH}>Korean title</th><th className={TH}>English title</th>
            <th className={TH}>Effective at</th><th className={TH}>Created by</th><th className={TH}>Created at</th>
          </tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.version}>
                <td className={TD}>
                  <button type="button" className="text-teal-700 underline dark:text-teal-400"
                    onClick={() => setViewing(t.version)}>v{t.version}</button>
                  {current === t.version && <Badge tone="ok">In effect</Badge>}
                </td>
                <td className={TD}>{t.titleKo}</td>
                <td className={TD}>{t.titleEn}</td>
                <td className={TD}><Time ms={t.effectiveAt} /></td>
                {/* 1판은 서버 마이그레이션(V520)이 넣어 관리자가 없다. */}
                <td className={TD}><AdminRef adminId={t.createdByAdminId} empty="System" /></td>
                <td className={TD}><Time ms={t.createdAt} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
      {viewing !== null && (
        <ViewTermsDialog version={viewing} effective={current} onClose={() => setViewing(null)} />
      )}
      {creating && <NewTermsDialog base={effective.data ?? null} onClose={() => setCreating(false)} />}
    </>
  )
}

/** 한 언어의 제목·본문. 본문은 글로만 — HTML 로 그리지 않는다(EA-52 와 같게). */
function Text({ lang, title, body }: { lang: string; title: string; body: string }) {
  return (
    <LangText lang={lang}>
      <p className="mb-2 font-medium">{title}</p>
      <Plain text={body} />
    </LangText>
  )
}

/** EA-66 — 판 보기(XM-102). 한국어·영어를 함께. */
function ViewTermsDialog({ version, effective, onClose }: {
  version: number
  effective: number | null
  onClose: () => void
}) {
  const q = useQuery({ queryKey: ['wallet-trade-terms', version], queryFn: () => getWalletTradeTerms(version) })
  const t = q.data
  return (
    <ViewDialog open title={`${WALLET_TRADE_TERMS} v${version}`} onClose={onClose}>
      <QueryState isPending={q.isPending} error={q.error}>
        {t && (
          <>
            <dl className="mb-3 grid grid-cols-[6rem_1fr] gap-1.5">
              <Row label="Effective at">
                <Time ms={t.effectiveAt} />
                {effective === t.version && <Badge tone="ok">In effect</Badge>}
              </Row>
              <Row label="Created by"><AdminRef adminId={t.createdByAdminId} empty="System" /></Row>
              <Row label="Created at"><Time ms={t.createdAt} /></Row>
            </dl>
            <Text lang="Korean" title={t.titleKo} body={t.bodyKo} />
            <Text lang="English" title={t.titleEn} body={t.bodyEn} />
          </>
        )}
      </QueryState>
    </ViewDialog>
  )
}

/** EA-67 — 새 판(XM-103). 처음 값은 시행 중 판(없으면 빈 칸 — EA-53 과 같게). */
function NewTermsDialog({ base, onClose }: { base: WalletTradeTerms | null; onClose: () => void }) {
  const qc = useQueryClient()
  const w = useWrite()
  const [titleKo, setTitleKo] = useState(base?.titleKo ?? '')
  const [bodyKo, setBodyKo] = useState(base?.bodyKo ?? '')
  const [titleEn, setTitleEn] = useState(base?.titleEn ?? '')
  const [bodyEn, setBodyEn] = useState(base?.bodyEn ?? '')
  const [effectiveAt, setEffectiveAt] = useState('')

  // 시행일은 「yyyy-mm-dd HH:mm:ss」(관리자 컴퓨터 시간)를 밀리초로(비우면 서버가 지금으로).
  const { ms: effectiveMs, bad: badDate } = effectiveMillis(effectiveAt)
  // 네 칸 모두 필수(서버 @NotBlank — 공백만이면 400).
  const blank = [titleKo, bodyKo, titleEn, bodyEn].some((s) => s.trim() === '')

  async function submit() {
    const send = async () => {
      const t = await createWalletTradeTerms({ titleKo, bodyKo, titleEn, bodyEn }, effectiveMs)
      setFlash({ tone: 'ok', text: `Created ${WALLET_TRADE_TERMS} v${t.version}.` })
      await qc.invalidateQueries({ queryKey: ['wallet-trade-terms'] })
    }
    await submitWrite(w, `${WALLET_TRADE_TERMS} — new version`, send, onClose)
  }

  return (
    <Confirm open wide title={`${WALLET_TRADE_TERMS} — new version`} action="Create" danger
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}
      disabled={blank || badDate}>
      <div className="flex flex-col gap-3">
        <Field label="Korean title (required · 200 chars)" value={titleKo} maxLength={200}
          onChange={(e) => setTitleKo(e.target.value)} />
        <TextArea label="Korean body (required · 200,000 chars)" value={bodyKo} maxLength={200000} rows={10}
          onChange={(e) => setBodyKo(e.target.value)} />
        <Field label="English title (required · 200 chars)" value={titleEn} maxLength={200}
          onChange={(e) => setTitleEn(e.target.value)} />
        <TextArea label="English body (required · 200,000 chars)" value={bodyEn} maxLength={200000} rows={10}
          onChange={(e) => setBodyEn(e.target.value)} />
        <DateTimeField label="Effective at (optional)" name="Effective at" value={effectiveAt} onChange={setEffectiveAt} />
        {badDate && <Notice tone="stop">The effective time cannot be read.</Notice>}
        <p className="text-xs text-slate-500">
          Saves as a new version. Old versions are not deleted. {WHERE}
        </p>
      </div>
    </Confirm>
  )
}
